import { useDebouncedValidate } from '@hooks/useDebouncedValidate.ts';
import { useFF } from '@hooks/useFF.ts';
import { useBicValidationContext } from '@pages/payments/fx/contexts/BicValidationContext.tsx';
import type { AddressMode } from '@pages/payments/utils/addressUtils.ts';
import {
	isValidBICString,
	matchesIbanBic,
	normalizeValidationKey,
} from '@pages/payments/utils/bicUtils.ts';
import { useQueryClient } from '@tanstack/react-query';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ValidateResult } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CC_FEATURE_FLAGS } from 'types/layout.ts';

import type { BicValidationResult } from './bicApi.ts';
import { validateBicApi, VALIDATION_GC_TIME } from './bicApi.ts';
import { setBicPopulatedFields } from './bicFieldPopulation.ts';

// Re-exported so BicValidationContext keeps its existing import path.
export type { BicValidationResult } from './bicApi.ts';

interface UseBicValidationCoreParams {
	prefix: string;
	bicValue?: string;
	ibanBicMaskRef?: RefObject<string | null>;
	patternError?: string;
	preserveAddressType?: boolean;
}

interface UseBicValidationCoreResult {
	validate: (value: string) => Promise<ValidateResult>;
	isValidating: boolean;
	bicData: BicValidationResult | null;
	clearBicData: () => void;
	warning: string | null;
	isLocked: boolean;
}

const useBicValidationCore = ({
	prefix,
	bicValue,
	ibanBicMaskRef,
	patternError,
	preserveAddressType,
}: UseBicValidationCoreParams): UseBicValidationCoreResult => {
	const { setValue, trigger, clearErrors, getValues } = useFormContext();
	const hasAddressAligned = useFF(CC_FEATURE_FLAGS.ADDRESS_ALIGNMENT);
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const [isValidating, setIsValidating] = useState(false);
	const [bicData, setBicData] = useState<BicValidationResult | null>(null);
	const [warning, setWarning] = useState<string | null>(null);
	const latestBicRef = useRef('');
	const hasTriggeredInitialValidation = useRef(false);
	const lastPopulatedBicRef = useRef('');

	// Clears any auto-populated fields + warning when the BIC is gone/invalid.
	const clearPopulated = useCallback(() => {
		setBicData(null);
		setWarning(null);
		if (lastPopulatedBicRef.current) {
			setBicPopulatedFields(
				setValue,
				prefix,
				undefined,
				hasAddressAligned ? 'STRUCTURED' : undefined,
			);
			lastPopulatedBicRef.current = '';
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prefix]);

	// True when the BIC doesn't match the IBAN's resolved mask.
	const checkMismatch = (value: string): boolean =>
		!!ibanBicMaskRef?.current && !matchesIbanBic(value, ibanBicMaskRef.current);

	// Applies a known BIC result to form state + warning; returns the RHF result.
	// Shared by the sync cache-hit fast-path and the debounced cache-miss branch.
	const applyBicResult = (value: string, result: BicValidationResult): ValidateResult => {
		if (!result.isValid) {
			clearPopulated();
			setWarning(t('payments.common.bicValidationFailed'));
			return true; // warning only — never an RHF error
		}
		setWarning(null);
		if (lastPopulatedBicRef.current !== value) {
			lastPopulatedBicRef.current = value;
			setBicData(result);
			if (hasAddressAligned) {
				setBicPopulatedFields(setValue, prefix, result, 'STRUCTURED');
			} else {
				const forcedType = preserveAddressType
					? (getValues(`${prefix}.address.type`) as AddressMode | undefined)
					: undefined;
				setBicPopulatedFields(setValue, prefix, result, forcedType);
			}
		}
		return checkMismatch(value) ? t('payments.common.swiftIbanMismatch') : true;
	};

	const fetchBic = async (value: string): Promise<ValidateResult> => {
		if (patternError && !isValidBICString(value)) {
			clearPopulated();
			return latestBicRef.current === value ? patternError : true;
		}
		setIsValidating(true);
		const normalized = normalizeValidationKey(value);
		try {
			const data = await queryClient.fetchQuery({
				queryKey: ['bic-details', normalized],
				queryFn: () => validateBicApi(normalized),
				staleTime: Infinity,
				gcTime: VALIDATION_GC_TIME,
			});

			if (latestBicRef.current !== value) {
				return true;
			}
			return applyBicResult(value, data);
		} finally {
			if (latestBicRef.current === value) {
				setIsValidating(false);
			}
		}
	};
	const clearBicError = useCallback(() => {
		clearErrors(`${prefix}.bic`);
	}, [clearErrors, prefix]);
	const debouncedFetchBic = useDebouncedValidate(fetchBic, undefined, clearBicError);

	const validate = useCallback(
		async (value: string) => {
			latestBicRef.current = value;

			if (!value) {
				clearPopulated();
				return true;
			}
			if (!isValidBICString(value)) {
				clearPopulated();
				return patternError ? debouncedFetchBic(value) : true;
			}
			// Sync fast-path: a cached BIC (and its mismatch) resolves instantly.
			// Must use the same normalized key the cache-miss fetch writes.
			const cached = queryClient.getQueryData<BicValidationResult>([
				'bic-details',
				normalizeValidationKey(value),
			]);
			if (cached) {
				return applyBicResult(value, cached);
			}
			// Cache miss: only the network fetch is debounced.
			return debouncedFetchBic(value);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryClient, clearPopulated, debouncedFetchBic, patternError, applyBicResult],
	);

	const clearBicData = useCallback(() => {
		setBicData(null);
		setWarning(null);
		latestBicRef.current = '';
		lastPopulatedBicRef.current = '';
	}, []);

	// Clear populated fields when the BIC value changes to something that won't
	// trigger validate (e.g., empty or wrong length) — `required` can short-circuit
	// before validate runs.
	useEffect(() => {
		if (!bicValue || !isValidBICString(bicValue)) {
			clearPopulated();
		}
	}, [bicValue, clearPopulated]);

	// Trigger validation on mount for pre-populated BIC values
	// (e.g., when editing an existing payment or loading a template).
	useEffect(() => {
		if (
			hasTriggeredInitialValidation.current ||
			!bicValue ||
			!isValidBICString(bicValue)
		) {
			return;
		}
		hasTriggeredInitialValidation.current = true;
		void trigger(`${prefix}.bic`);
	}, [bicValue, prefix, trigger]);

	return {
		validate,
		isValidating,
		bicData,
		clearBicData,
		warning,
		isLocked: !!bicData,
	};
};

export const useBicValidation = (
	prefix: string,
	ibanBicMaskRef?: RefObject<string | null>,
	patternError?: string,
) => {
	const bicValue = useWatch({ name: `${prefix}.bic` }) as string | undefined;
	return useBicValidationCore({ prefix, bicValue, ibanBicMaskRef, patternError });
};

const BIC_POPULATED_FIELDS = ['.name', '.address'] as const;

export const useFXBicValidation = (
	fieldKey: string,
	ibanBicMaskRef?: RefObject<string | null>,
) => {
	const { setValue } = useFormContext();
	const { t } = useTranslation();
	const prefix = fieldKey.replace(/\.bic$/, '');
	const bicValue = useWatch({ name: fieldKey }) as string | undefined;
	const bicContext = useBicValidationContext();

	useEffect(
		() => () => {
			BIC_POPULATED_FIELDS.forEach((suffix) => {
				setValue(`${prefix}${suffix}`, undefined);
			});
		},
		[prefix, setValue],
	);

	const result = useBicValidationCore({
		prefix,
		bicValue: fieldKey ? bicValue : undefined,
		ibanBicMaskRef,
		patternError: t('payments.common.bicPatternValidation'),
		preserveAddressType: true,
	});

	const prevBicNameRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!bicContext) {
			return;
		}
		const currentName = result.bicData?.name;
		if (prevBicNameRef.current === currentName) {
			return;
		}
		prevBicNameRef.current = currentName;
		bicContext.setBicResult(prefix, result.bicData);
	}, [prefix, result.bicData, bicContext]);

	return result;
};
